import axios from "axios";

const DATALAYER_PUSH_DELAY = 1500;

// Retrasa el push para que llegue al dataLayer después de eventos internos de GTM
// (gtm.click, gtm.historyChange-v2, etc.) disparados en la misma carga de página.
const pushDelayed = (eventData, { clearEcommerce = false } = {}) => {
  setTimeout(() => {
    window.dataLayer = window.dataLayer || [];
    if (clearEcommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(eventData);
  }, DATALAYER_PUSH_DELAY);
};

const GTM_CALLBACK_TIMEOUT = 2000;

// Para un evento seguido de una recarga completa de la página: se envía con el mismo
// retraso que pushDelayed (para que siga siendo lo último en llegar al dataLayer) y la
// promesa resuelve cuando GTM terminó de disparar sus etiquetas, o a los 2 s si GTM no
// responde (bloqueado por un adblocker, por ejemplo). Quien llama navega al resolver.
const pushBeforeNavigation = (eventData) =>
  new Promise((resolve) => {
    setTimeout(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        ...eventData,
        eventCallback: () => resolve(),
        eventTimeout: GTM_CALLBACK_TIMEOUT,
      });
      setTimeout(resolve, GTM_CALLBACK_TIMEOUT);
    }, DATALAYER_PUSH_DELAY);
  });

// GA4: "price" es el precio unitario que paga el cliente (ya con descuento) y
// "discount" el monto descontado por unidad.
const toPricing = (listPrice, finalPrice) => {
  const base = Number(listPrice) || 0;
  const final = Number(finalPrice) || 0;
  const price = final > 0 && final < base ? final : base;
  return { price, discount: base - price };
};

// Producto del catálogo o del carrito de invitado (localStorage): unit_price es el
// precio de lista; discount_valor y discount_tag_valor son el precio final cuando hay
// descuento (en el mismo orden en que los muestra el detalle del producto).
export const getProductPricing = (product) =>
  toPricing(
    product.unit_price,
    Number(product.discount_valor) || Number(product.discount_tag_valor)
  );

// Fila del carrito con sesión (API /cart): price es el precio de lista, discount el
// monto descontado y discount_tag el precio final del descuento por etiqueta.
export const getCartItemPricing = (cartItem) => {
  const listPrice = Number(cartItem.price) || 0;
  const discount = Number(cartItem.discount) || 0;
  return toPricing(listPrice, discount > 0 ? listPrice - discount : cartItem.discount_tag);
};

// Línea de producto a partir de un producto del catálogo o del carrito de invitado.
export const lineFromProduct = (product, quantity) => ({
  productId: product.id,
  name: product.name,
  variant: product.variant,
  quantity: Number(quantity) || 1,
  ...getProductPricing(product),
});

// Línea de producto a partir de una fila del carrito con sesión: su "id" es el de la
// fila del carrito, el del producto viene en product_id.
export const lineFromCartItem = (cartItem) => ({
  productId: cartItem.product_id,
  name: cartItem.name,
  variant: cartItem.variant,
  quantity: Number(cartItem.quantity) || 1,
  ...getCartItemPricing(cartItem),
});

// Categoría y nombre de marca de cada producto, consultados una sola vez por sesión.
// Si la consulta falla el evento se envía igual, sin esos dos datos.
const productInfoRequests = new Map();

const getProductInfo = (productId) => {
  if (!productInfoRequests.has(productId)) {
    const request = axios
      .get('https://api-rest.egoi.com.co/api/v1/products/details_id/' + productId, {
        headers: {
          Accept: 'application/json',
        },
      })
      .then(({ data }) => ({
        category: data?.category_name ?? "",
        brand: data?.data_brand?.[0]?.name ?? "",
      }))
      .catch((error) => {
        productInfoRequests.delete(productId);
        console.warn("No se pudo obtener categoría y marca del producto " + productId, error);
        return { category: "", brand: "" };
      });
    productInfoRequests.set(productId, request);
  }
  return productInfoRequests.get(productId);
};

const buildItem = async (line, options = {}) => {
  const { category, brand } = await getProductInfo(line.productId);
  const item = {
    item_id: 'egoi-' + String(line.productId),
    item_name: line.name ?? "",
    item_brand: brand,
    item_category: category,
    item_variant: line.variant ?? "",
    price: line.price,
    quantity: line.quantity,
    discount: line.discount,
    coupon: options.coupon ?? "",
    affiliation: "Egoi",
  };

  if (options.index !== undefined) item.index = options.index;
  if (options.item_list_name !== undefined) item.item_list_name = options.item_list_name;
  if (options.item_list_id !== undefined) item.item_list_id = options.item_list_id;

  return item;
};

export const buildItems = (lines, options) =>
  Promise.all(lines.map((line) => buildItem(line, options)));

const sumValue = (items) =>
  items.reduce((sum, item) => sum + item.price * item.quantity, 0);

export const pushAddToCart = async (product, qtyAdded, { beforeNavigation = false } = {}) => {
  const item = await buildItem(lineFromProduct(product, qtyAdded));
  const eventData = {
    event: "add_to_cart",
    ecommerce: {
      currency: "COP",
      value: sumValue([item]),
      items: [item],
    },
  };

  if (beforeNavigation) return pushBeforeNavigation(eventData);
  pushDelayed(eventData, { clearEcommerce: true });
};

export const pushRemoveFromCart = async (line) => {
  const item = await buildItem(line);

  pushDelayed(
    {
      event: "remove_from_cart",
      ecommerce: {
        currency: "COP",
        value: sumValue([item]),
        items: [item],
      },
    },
    { clearEcommerce: true }
  );
};

export const pushSelectItem = async (product, options = {}) => {
  const item = await buildItem(lineFromProduct(product, options.quantity), options);

  pushDelayed(
    {
      event: "select_item",
      ecommerce: {
        items: [item],
        item_list_name: options.item_list_name ?? "",
        item_list_id: options.item_list_id ?? "",
      },
    },
    { clearEcommerce: true }
  );
};

export const pushViewItem = async (product, options = {}) => {
  const item = await buildItem(lineFromProduct(product, options.quantity), options);

  console.log("Pushing view_item with item:", item);

  pushDelayed(
    {
      event: "view_item",
      ecommerce: {
        currency: options.currency ?? "COP",
        value: sumValue([item]),
        items: [item],
      },
    },
    { clearEcommerce: true }
  );
};

export const pushViewCart = async (lines, currency = "COP") => {
  if (!lines || lines.length === 0) return;

  const items = await buildItems(lines);

  pushDelayed(
    {
      event: "view_cart",
      ecommerce: {
        currency,
        value: sumValue(items),
        items,
      },
    },
    { clearEcommerce: true }
  );
};

export const pushBeginCheckout = async (lines, currency = "COP") => {
  if (!lines || lines.length === 0) return;

  const items = await buildItems(lines);

  pushDelayed(
    {
      event: "begin_checkout",
      ecommerce: {
        currency,
        value: sumValue(items),
        items,
      },
    },
    { clearEcommerce: true }
  );
};

export const pushViewHome = () => {
  // Espera a que la página termine de cargar (window "load") para que "home"
  // sea el último evento en llegar al dataLayer, después de todos los eventos
  // internos de GTM (gtm.js, gtm.dom, gtm.load, consentimiento, etc.).
  const fire = () => pushDelayed({ event: "home" });

  if (document.readyState === "complete") {
    fire();
  } else {
    window.addEventListener("load", fire, { once: true });
  }
};

// Evento recomendado de GA4 para contenido compartido.
export const pushShare = (product, method) => {
  pushDelayed({
    event: "share",
    method,
    content_type: "product",
    item_id: 'egoi-' + String(product.id),
  });
};

// Clic en "escríbenos por WhatsApp" desde un producto (evento personalizado; en GTM
// se puede marcar como conversión de contacto).
export const pushWhatsAppClick = (product) => {
  pushDelayed({
    event: "whatsapp_click",
    item_id: 'egoi-' + String(product.id),
    item_name: product.name ?? "",
  });
};

export const pushLogin = (method = "Google") => {
  pushDelayed({ event: "login", method });
};

export const pushSignUp = (method = "Google") => {
  pushDelayed({ event: "sign_up", method });
};
